import { defineEntitySource, defineLocalEntitySource } from '@/shared/form/entity-source';
import {
  listBankOptions,
  listTenantOptions,
  getEnumValues,
  listCosifAccounts,
} from './reference.api';
type Item<F extends (...args: never[]) => Promise<{ data: readonly unknown[] }>> = Awaited<
  ReturnType<F>
>['data'][number];
export const bankSource = defineEntitySource<Item<typeof listBankOptions>>({
  key: ['reference', 'bank'],
  search: async (query, signal) => (await listBankOptions(query || undefined, signal)).data,
  toOption: (bank) => {
    const code = String(bank.code ?? bank.ispb ?? '');
    return { value: code, label: `${code} - ${bank.name ?? bank.codeDisplay ?? ''}` };
  },
});
export const tenantSource = defineEntitySource({
  key: ['reference', 'tenant'],
  search: async (query, signal) =>
    (await listTenantOptions(signal)).filter((item) =>
      item.name.toLowerCase().includes(query.toLowerCase()),
    ),
  toOption: (item) => ({ value: item.id, label: item.name }),
});
export const enumSource = (name: string, size = 10) =>
  defineEntitySource({
    key: ['reference', 'enum', name, size],
    search: (search, signal) => getEnumValues(name, { size, search }, signal),
    toOption: (item) => ({ value: item.value, label: item.name }),
  });

/**
 * COSIFs de transferência com conta de débito vinculada, como o seletor de lote do legado
 * (`ProductDebitAccountBatchFormContainer.tsx:50-57`): uma consulta e filtro local; `description`
 * nula vira `''` (`o.label ?? ''`, R2 da revisão da F1a).
 */
export const cosifDebitAccountSource = defineLocalEntitySource({
  key: ['reference', 'cosif', 'debit-account'],
  load: (signal) => listCosifAccounts(signal),
  include: (item) => !!item.externalAccountId,
  text: (item) => (typeof item.description === 'string' ? item.description : ''),
  toOption: (item) => ({ value: item.code, label: typeof item.description === 'string' ? item.description : '' }),
});

/** Mapper de useBankList({compe:true}): código inválido usa ISPB; rótulo usa codeDisplay (A12). */
export const bankCompeSource = defineEntitySource<Item<typeof listBankOptions>>({
  key: ['reference', 'bank', 'compe'],
  search: async (query, signal) => (await listBankOptions(query || undefined, signal)).data,
  toOption: (bank) => {
    const value = bank.code == null || bank.code === 0 ? bank.ispb : bank.code;
    return { value: String(value), label: `${String(value)} - ${String(bank.codeDisplay)}` };
  },
});

/** Banco sem COMPE do Pix; código ausente/zero usa ISPB (contexts/bankList.tsx:37-44). */
export const bankPixSource = defineEntitySource<Item<typeof listBankOptions>>({
  key: ['reference', 'bank', 'pix'],
  search: async (query, signal) => (await listBankOptions(query || undefined, signal, false)).data,
  toOption: (bank) => {
    const value = bank.code == null || bank.code === 0 ? bank.ispb : bank.code;
    return { value: String(value), label: `${String(value)} - ${String(bank.codeDisplay)}` };
  },
});

/** Mesmo fio/mapeamento, com a chave de cache histórica do chamador (U50). */
export const createBankPixSource = (key: readonly unknown[]) => defineEntitySource<Item<typeof listBankOptions>>({
  key,
  search: bankPixSource.search,
  toOption: bankPixSource.toOption,
});
